/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The aanbieder-dossier: the structured "wat u moet weten" facts about an
 * inboekdienst that the comparison table cannot carry — by when you must
 * cancel, what happens after the term, what leaving early costs, when you are
 * paid, the price model, a minimum volume and what a visitor needs to sign up.
 * The comparer answers "what is the deal"; these answer "what is the catch" and
 * "does it fit me", the two questions a visitor asked the provider page and got
 * nothing for until 2026-09-03.
 *
 * Plain ESM JavaScript on purpose (the signupLeads.js pattern): consumed by the
 * client bundle (provider page, comparer, portal, admin drawer), the server
 * (partner routes, pending-update apply, the admin providers save) AND
 * scripts/prerender.mjs, which cannot import TypeScript. Keep it
 * dependency-free but for the offer's pricing rule (src/lib/offerPricing.js,
 * itself import-free), which deviatingModelOf reads the model through, and the
 * payout vocabulary (src/lib/payout.js, import-free too), an offer's rhythm's.
 *
 * Shape on the provider record (providers_database.json), PER PROVIDER:
 *
 *   provider.dossier = {
 *     cancelDeadline: { value: 'before_date', amount: 1001, note: '',
 *                       source: 'editorial', at: '2026-10-07T10:00:00.000Z',
 *                       sourceUrl: 'https://…/voorwaarden.pdf', quote: '…' },
 *     exitCost:       { value: 'fee', amount: 75, note: 'per resterend jaar', … },
 *     signupRequirements: { value: ['ean', 'meter_reading'], … },
 *   }
 *
 * and PER OFFER, for the three binding facts and the payout rhythm
 * (OFFER_TERM_FIELDS), an override with the same entry shape that the row
 * inherits nothing over:
 *
 *   contract.terms = { cancelDeadline?, afterTerm?, exitCost?, payoutFrequency? }
 *
 * The rhythm has no entry on the provider: what a provider pays out by is the
 * scalar `provider.payoutFrequency`, and an offer that differs carries its own
 * (2026-11-03, offerPayoutFrequency).
 *
 * Every entry carries its PROVENANCE. `source` is 'provider' when the value
 * came in through an approved portal submission and 'editorial' when an admin
 * typed it in the drawer (or the reviewed fill script wrote it); `at` is when
 * that happened. The public page prints both ("Opgegeven door de aanbieder,
 * bevestigd op …"), so a value without a source is not a value this module lets
 * through. The EVIDENCE (`sourceUrl` + `quote`, 2026-10-07; plus `sourceKind` —
 * website, document, mail or telefoon — since 2026-10-08) is optional on the
 * record and required by the fill; it is never emitted by `publicDossier`,
 * `publicOfferTerms` or the partner payload, and `dossierValuesEqual` ignores it.
 *
 * THREE STATES, all visible (plan 2026-10-07 §4.1): an entry with a real code
 * is KNOWN; the code `not_stated` means "the terms were read and say nothing
 * about it" (editorial-only — a provider never files it); NO entry means "not
 * checked". A field that is absent is never guessed, never rendered as a fact.
 *
 * Values are ENUM CODES, not prose: reviewable in one glance, comparable across
 * providers, translatable (the site renders them through i18n `dossier.*`
 * keys) and machine-readable. `other` is the escape hatch and REQUIRES a note.
 *
 * Retired 2026-10-07 (plan-voorwaarden-vergelijking §4.1): `noticePeriod`,
 * `exitFee` and `firstPayout`. Since 2026-10-14 this module does not know
 * them at all: no spec, no label, no change field. An entry a record still
 * carries is read by nothing and stays until the next admin save drops it
 * (stampDossierProvenance keeps the fields this module knows).
 */

import { normalizeOfferPricing, ownOfferPricing } from './offerPricing.js';
import { PAYOUT_FREQUENCIES } from './payout.js';

/**
 * Record fields, in the order every surface lists them — the order of the
 * visitor's questions (opzeggen, na de looptijd, eerder stoppen, uitbetaling,
 * wie boekt in, prijsmodel, minimum, aanmelden).
 */
export const DOSSIER_FIELDS = /** @type {const} */ ([
  'cancelDeadline',
  'afterTerm',
  'exitCost',
  'payoutTiming',
  'inboeker',
  'priceModel',
  'minimumVolume',
  'signupRequirements',
]);

/**
 * @typedef {Object} DossierFieldSpec
 * @property {readonly string[]} values  every code the record may hold, LEGACY codes included
 *   (so a stored one still normalises), in the order an editor offers them
 * @property {boolean} multi             several codes at once (the derived signup list only)
 * @property {Readonly<Record<string, DossierUnit>>} amountFor  codes that take a number, with its unit
 * @property {readonly string[]} noteFor codes that REQUIRE a note
 * @property {readonly string[]} legacy  codes that are still read but never offered, never
 *   written (isWritableDossierCode) and never public (publicDossier drops them)
 * @property {readonly string[]} editorialOnly  codes only the redactie may write: the portal
 *   submit answers `400 invalid_value` (isWritableDossierCode with audience 'provider')
 * @property {readonly string[]} [positiveFor]  codes whose amount must be above zero (an exit
 *   `fee` of € 0 is no fee — `none` says that)
 */

const NO_CODES = Object.freeze(/** @type {string[]} */ ([]));
const NOTE_FOR_OTHER = Object.freeze(['other']);
/**
 * "The terms were read completely and say nothing about this" — a FACT about
 * the terms, offered on every editable field, written only by the redactie
 * (the fill requires the read terms' URL as its evidence). Distinct from an
 * absent entry, which means "not checked".
 */
const EDITORIAL_ONLY = Object.freeze(['not_stated']);

/**
 * Per field: the allowed codes, whether several may be chosen, which codes
 * take a number (with its unit — the unit decides the formatting and the
 * validation of the amount), which need a note, and which codes are legacy or
 * editorial-only.
 *
 * @type {Readonly<Record<string, DossierFieldSpec>>}
 */
export const DOSSIER_SPEC = Object.freeze({
  // The cancel DEADLINE before year end (2026-10-07). The NEa allows one
  // inboeker per aansluiting per calendar year and no switch during it, so a
  // "notice period" never meant you could leave: what differs per provider is
  // the first day on which a cancellation is TOO LATE to stop per 1 January.
  // `before_date` stores that day as MMDD (`1001` = "vóór 1 oktober"); `none`
  // = you can cancel up to and including 31 December. `101` is refused — a
  // deadline of 1 January is `none`.
  cancelDeadline: Object.freeze({
    values: Object.freeze(['none', 'before_date', 'other', 'not_stated']),
    multi: false,
    amountFor: Object.freeze({ before_date: 'month_day' }),
    noteFor: NOTE_FOR_OTHER,
    legacy: NO_CODES,
    editorialOnly: EDITORIAL_ONLY,
  }),
  // `renews_yearly` (2026-09-18): the clause most terms actually carry is
  // "stilzwijgend verlengd met telkens één (kalender)jaar" — whatever the first
  // term was. Without a code for it, a 3-year offer renewing per year was filed
  // under `renews_same_term`, and a card then promised a renewal of three years.
  // `renews_monthly` is LEGACY since 2026-10-07: "after the term cancellable
  // monthly" cannot exist under the one-inboeker-per-calendar-year rule.
  afterTerm: Object.freeze({
    values: Object.freeze([
      'ends',
      'renews_yearly',
      'renews_same_term',
      'other',
      'not_stated',
      'renews_monthly',
    ]),
    multi: false,
    amountFor: Object.freeze({}),
    noteFor: NOTE_FOR_OTHER,
    legacy: Object.freeze(['renews_monthly']),
    editorialOnly: EDITORIAL_ONLY,
  }),
  // What LEAVING EARLY costs (2026-10-07, replaces `exitFee`): nothing, the
  // year's payout, a fee — whose note says per what ("per resterend jaar",
  // "eenmalig"), so a bare amount can never read as a one-off sum.
  exitCost: Object.freeze({
    values: Object.freeze(['none', 'forfeit_year_payout', 'fee', 'other', 'not_stated']),
    multi: false,
    amountFor: Object.freeze({ fee: 'eur' }),
    noteFor: Object.freeze(['other', 'fee']),
    legacy: NO_CODES,
    editorialOnly: EDITORIAL_ONLY,
    // "Eerder stoppen kost vanaf € 0" would be an amber catch that costs nothing.
    positiveFor: Object.freeze(['fee']),
  }),
  // WHEN the money comes (2026-10-07, replaces `firstPayout`): during the
  // charging year (an advance, or per inboeking) or only after it. A payout
  // during the year whose terms allow a clawback is `other` with a note (D11).
  payoutTiming: Object.freeze({
    values: Object.freeze(['during_year', 'after_year', 'other', 'not_stated']),
    multi: false,
    amountFor: Object.freeze({}),
    noteFor: NOTE_FOR_OTHER,
    legacy: NO_CODES,
    editorialOnly: EDITORIAL_ONLY,
  }),
  // WHO REGISTERS the kWh with the NEa (D24, 2026-10-07): the provider on its
  // own NEa account (`self`) or another inboekdienstverlener in the back end
  // (`other` — NOT the escape hatch here: a real answer, whose optional note
  // names that party when the provider itself publishes it). Researched, never
  // proposed: the whole field is editorial-only (EDITORIAL_DOSSIER_FIELDS). An
  // unfiled one on a provider that is not on the NEa list reads as `other`
  // (termsTone's neaListed rule) — the list is closed since 1 October 2026.
  inboeker: Object.freeze({
    values: Object.freeze(['self', 'other', 'not_stated']),
    multi: false,
    amountFor: Object.freeze({}),
    noteFor: NO_CODES,
    legacy: NO_CODES,
    editorialOnly: Object.freeze(['self', 'other', 'not_stated']),
  }),
  // The BASIS of the customer's payment, not a price: commissie and
  // servicekosten are per aanbod. `subscription` (2026-09-16) deliberately has
  // no amountFor entry — the monthly/yearly sum belongs on the aanbod row, and
  // normalizeDossierValue therefore drops a stray amount rather than storing a
  // figure no surface would render.
  priceModel: Object.freeze({
    values: Object.freeze([
      'market_minus_commission',
      'fixed_per_kwh',
      'fixed_per_ere',
      'subscription',
      'other',
      'not_stated',
    ]),
    multi: false,
    amountFor: Object.freeze({ fixed_per_kwh: 'eur_per_kwh', fixed_per_ere: 'eur_per_ere' }),
    noteFor: NOTE_FOR_OTHER,
    legacy: NO_CODES,
    editorialOnly: EDITORIAL_ONLY,
  }),
  minimumVolume: Object.freeze({
    values: Object.freeze(['none', 'kwh_per_year', 'charge_points', 'other', 'not_stated']),
    multi: false,
    amountFor: Object.freeze({ kwh_per_year: 'kwh', charge_points: 'count' }),
    noteFor: NOTE_FOR_OTHER,
    legacy: NO_CODES,
    editorialOnly: EDITORIAL_ONLY,
  }),
  // DERIVED since 2026-09-16 (see DERIVED_DOSSIER_FIELDS): composed from the
  // CRM's priced `leadSettings.requiredItems`, not typed by anyone. The spec is
  // unchanged on purpose — `mid_certificate` included, even though no item kind
  // maps onto it any more — because entries stored before that date must still
  // normalise, render on a public page and print in an old review mail.
  signupRequirements: Object.freeze({
    values: Object.freeze([
      'ean',
      'meter_reading',
      'serial_number',
      'type_plate_photo',
      'mid_certificate',
      'kvk',
      'energy_contract',
      'other',
    ]),
    multi: true,
    amountFor: Object.freeze({}),
    noteFor: NOTE_FOR_OTHER,
    legacy: NO_CODES,
    editorialOnly: NO_CODES,
  }),
  // The payout RHYTHM of ONE OFFER (2026-11-03), never of a provider: that one
  // is the scalar `provider.payoutFrequency`, so this field is not in
  // DOSSIER_FIELDS. Its codes are the four the comparer can print (D4); a choice
  // between two rhythms is the most frequent one, with the other in the note
  // ("Per halfjaar", "of per jaar, naar keuze"). Set by the redactie only.
  payoutFrequency: Object.freeze({
    values: PAYOUT_FREQUENCIES,
    multi: false,
    amountFor: Object.freeze({}),
    noteFor: NO_CODES,
    legacy: NO_CODES,
    editorialOnly: PAYOUT_FREQUENCIES,
  }),
});

/**
 * What an OFFER may set for itself (D6, editorial-only this release): the three
 * binding facts and, since 2026-11-03, its payout rhythm. A row inherits every
 * one it does not set: the binding facts from the provider's dossier, the rhythm
 * from the provider's scalar (offerPayoutFrequency).
 */
export const OFFER_TERM_FIELDS = /** @type {const} */ ([
  'cancelDeadline',
  'afterTerm',
  'exitCost',
  'payoutFrequency',
]);

/** A ROW's dossier: the record's fields, plus the rhythm an offer may bring (withOfferTerms). */
const ROW_DOSSIER_FIELDS = [...DOSSIER_FIELDS, 'payoutFrequency'];

/**
 * The usual deadline: a cancellation must be in before 1 December — one month
 * before the year ends. The market norm, so neutral; an earlier one is a catch.
 */
export const COMMON_CANCEL_DEADLINE = 1201;

/**
 * i18n LEAF overrides. Both tables exist for the same reason: reworded copy
 * ships under a NEW key, because an `i18n_admin/` override on acc/prd freezes
 * the VALUE of a key that has already shipped (docs/i18n-masking.md) — an
 * in-place edit of a live key is therefore invisible on exactly the two
 * environments that matter. The lookup path stays derivable from the field (and
 * the code), so nothing downstream has to know which of them was reworded.
 *
 * Kept HERE rather than in the editor that renders them: the same leaf is read
 * by the portal editor, the public profile renderer and the baseline-coverage
 * test, and a second copy is how two of the three end up on an old key.
 */

/** Hint under a dossier field: `partner_portal.dossier.hint.<leaf>`. */
export const HINT_KEY = Object.freeze({
  // Reworded 2026-09: the field is the BASIS of the payment, not a price —
  // commissie and servicekosten live on the aanbod row.
  priceModel: 'priceModel_v2',
});

/**
 * A reworded-leaf table read by any field and code: field → code → i18n leaf.
 *
 * @typedef {Partial<Record<string, Partial<Record<string, string>>>>} LabelLeafTable
 */

/** Value label of a dossier code: `dossier.value.<field>.<leaf>`. */
export const DOSSIER_VALUE_LABEL_KEY = Object.freeze({
  // Reworded 2026-09-16 with the field itself: the three labels said WHAT the
  // number is, not what the customer's payment is BASED on.
  priceModel: Object.freeze({
    market_minus_commission: 'market_minus_commission_v2',
    fixed_per_kwh: 'fixed_per_kwh_v2',
    fixed_per_ere: 'fixed_per_ere_v2',
  }),
});

/**
 * Value labels as a VISITOR reads them, for the codes whose shared wording
 * addresses the provider. `dossier.value.*` is read by two audiences: the
 * portal (select options, the review read-back), where "uw commissie" is the
 * provider's own commission — and the public profile page, where a visitor has
 * no commission at all and read the same words about themselves. Only the
 * public renderer asks for `audience: 'visitor'`; every other caller keeps the
 * wording it had.
 */
export const DOSSIER_VISITOR_VALUE_LABEL_KEY = Object.freeze({
  priceModel: Object.freeze({
    market_minus_commission: 'market_minus_commission_visitor',
  }),
});

/**
 * The i18n leaf for one field's hint — the field name itself unless the hint
 * has been reworded.
 *
 * @param {string} field
 * @returns {string}
 */
export function dossierHintKey(field) {
  return /** @type {Partial<Record<string, string>>} */ (HINT_KEY)[field] ?? field;
}

/**
 * The i18n leaf for one code of one field — the code itself unless it has been
 * reworded. `DOSSIER_VALUE_LABELS_NL` stays keyed by CODE: it is the Dutch
 * admin/e-mail table, which has no i18n tree and no masking problem.
 *
 * @param {string} field
 * @param {string} code
 * @param {'provider' | 'visitor'} [audience]  who reads the label; defaults to
 *   the portal's wording (see DOSSIER_VISITOR_VALUE_LABEL_KEY)
 * @returns {string}
 */
export function dossierValueLabelKey(field, code, audience = 'provider') {
  const visitorLeaf =
    audience === 'visitor'
      ? /** @type {LabelLeafTable} */ (DOSSIER_VISITOR_VALUE_LABEL_KEY)[field]?.[code]
      : undefined;
  return (
    visitorLeaf ?? /** @type {LabelLeafTable} */ (DOSSIER_VALUE_LABEL_KEY)[field]?.[code] ?? code
  );
}

/**
 * `month_day` (2026-10-07) is a calendar day without a year, as the integer
 * MMDD: `1001` = 1 October, `930` = 30 September. A deadline repeats every year,
 * so it carries no year — and an integer keeps the amount slot one type.
 *
 * @typedef {'eur' | 'eur_per_kwh' | 'eur_per_ere' | 'kwh' | 'count' | 'month_day'} DossierUnit
 */
/**
 * The record's shapes — ONE definition with the server, the admin and the
 * portal (src/types/api/providerTerms.ts). A stored fact is the editor's shape:
 * the public entry (value, amount — only for codes that take one, see
 * DOSSIER_SPEC.amountFor —, a one-line note of at most DOSSIER_NOTE_MAX, required
 * for `other`, the source and the ISO timestamp of its act) plus its evidence
 * (sourceUrl, quote, verifiedOn, sourceKind), which is never public.
 *
 * @typedef {import('../types/api/providerTerms.js').DossierSource} DossierSource
 * @typedef {import('../types/api/providerTerms.js').EvidenceSourceKind} EvidenceSourceKind
 * @typedef {import('../types/api/providerTerms.js').EditorDossierEntry} DossierEntry
 */

export const DOSSIER_SOURCES = /** @type {const} */ (['provider', 'editorial']);
export const DOSSIER_NOTE_MAX = 200;
/** Upper bound on any amount — a typo guard, not a business rule. */
export const DOSSIER_AMOUNT_MAX = 1_000_000;
/** Evidence caps — the listing URL cap and the editorial-bullet quote cap. */
export const DOSSIER_EVIDENCE_URL_MAX = 500;
export const DOSSIER_QUOTE_MAX = 500;

/**
 * HOW a piece of evidence was read (2026-10-08): the provider's `website`, a
 * published `document` (the terms as a pdf), a `mail` from the provider or a
 * `telefoon`-gesprek with it. A website and a document have an address to point
 * at, so their `sourceUrl` is required; a mail and a call have none, so the URL
 * is optional there and the quote (with its date) IS the proof. Absent means
 * `website`: that is how every fact filed before the kind existed was proved.
 * Never public.
 */
export const EVIDENCE_SOURCE_KINDS = /** @type {const} */ ([
  'website',
  'document',
  'mail',
  'telefoon',
]);

/**
 * Does evidence of this kind — absent or unknown reads as `website` — need a
 * source URL? Everything but a mail and a call does.
 *
 * @param {unknown} kind
 * @returns {boolean}
 */
export function evidenceKindNeedsUrl(kind) {
  return kind !== 'mail' && kind !== 'telefoon';
}

/**
 * A known evidence kind, trimmed — or null (the kind is then absent: `website`).
 *
 * @param {unknown} raw
 * @returns {EvidenceSourceKind | null}
 */
export function normalizeEvidenceKind(raw) {
  if (typeof raw !== 'string') return null;
  const kind = raw.trim();
  return /** @type {readonly string[]} */ (EVIDENCE_SOURCE_KINDS).includes(kind)
    ? /** @type {EvidenceSourceKind} */ (kind)
    : null;
}

/** The pattern of the one party the site never names (any case, any inflection). */
const RESTRICTED_NAME = /joul(?:o|ers?)/i;

/**
 * Does this text name the one party the site never names (the standing rule
 * since 2026-07-27, whatever a provider's own pages say — two compared
 * providers book through it, so it turns up in research notes)? ONE rule for
 * every path that writes text a visitor can read: a dossier note
 * (normalizeDossierValue), a redactie point's lines, quote and source
 * (normalizeEditorialBullet), the review's note (normalizeTermsReview). So
 * the admin save, the approval of a portal row and the fill all refuse it. A
 * fact's EVIDENCE may quote it: that stays on the record and is never public.
 *
 * ONE exception since 2026-10-08 (Jasper: "name Joulo when it is the other
 * inboeker", acc and prd alike): the `inboeker` fact's note IS the back-end
 * party's name ("Boekt in via {name}"), and a visitor is told who really
 * registers the kWh — so that one note may carry the name. Nothing else may.
 *
 * @param {unknown} text
 * @returns {boolean}
 */
export function containsRestrictedName(text) {
  return typeof text === 'string' && RESTRICTED_NAME.test(text);
}

/**
 * DERIVED facts: fields the platform composes rather than anyone editing.
 * `signupRequirements` became one on 2026-09-16 (decision E1): the list of what
 * a visitor must bring is now the priced `leadSettings.requiredItems` the CRM
 * owns, projected into a dossier entry by `leadSignupDossierEntry` in
 * src/lib/leadItems.js. The field keeps its spec, its codes and its labels —
 * entries stored before that date must still normalise and render, and an old
 * review mail must still be able to print one — it simply has no editor and no
 * review row any more.
 */
export const DERIVED_DOSSIER_FIELDS = /** @type {const} */ (['signupRequirements']);

/** @param {string} field */
export function isDerivedDossierField(field) {
  return /** @type {readonly string[]} */ (DERIVED_DOSSIER_FIELDS).includes(field);
}

/**
 * EDITORIAL-ONLY fields: written by the redactie (the admin drawer and the
 * reviewed fill script), never proposed by a provider — so no `prov:dossier…`
 * change field, no row in the portal editor, and isWritableDossierCode answers
 * false for the portal on every code. `inboeker` (D24) is an investigative fact
 * about the provider's back end, read from its own publications.
 */
export const EDITORIAL_DOSSIER_FIELDS = /** @type {const} */ (['inboeker']);

/** @param {string} field */
export function isEditorialDossierField(field) {
  return /** @type {readonly string[]} */ (EDITORIAL_DOSSIER_FIELDS).includes(field);
}

/**
 * Is `other` this field's ESCAPE HATCH — prose whose note IS the fact? True on
 * every field that requires a note for it; false on `inboeker`, where `other`
 * is a real answer ("another inboekdienstverlener") and its note only names
 * that party. Renderers print an escape hatch's note as the value and a coded
 * fact's note as a qualifier.
 *
 * @param {string} field
 * @param {string} code
 */
export function isProseCode(field, code) {
  const spec = DOSSIER_SPEC[field];
  return code === 'other' && !!spec && spec.noteFor.includes('other');
}

/**
 * The wire names of the EDITABLE fields as `prov:<field>` review rows, mapped
 * to the record field they write. Prefixed so they can never collide with a
 * top-level provider scalar, and so the row id says what kind of value it is.
 * A derived field and an editorial-only field are deliberately absent — a
 * partner may propose neither.
 */
export const DOSSIER_CHANGE_FIELDS = Object.freeze({
  dossierCancelDeadline: 'cancelDeadline',
  dossierAfterTerm: 'afterTerm',
  dossierExitCost: 'exitCost',
  dossierPayoutTiming: 'payoutTiming',
  dossierPriceModel: 'priceModel',
  dossierMinimumVolume: 'minimumVolume',
});
/** The change fields in DOSSIER_FIELDS order (the review-row order). */
export const DOSSIER_CHANGE_FIELD_LIST = /** @type {const} */ ([
  'dossierCancelDeadline',
  'dossierAfterTerm',
  'dossierExitCost',
  'dossierPayoutTiming',
  'dossierPriceModel',
  'dossierMinimumVolume',
]);

/** A record field this module knows. @param {string} field */
export function isDossierField(field) {
  return Object.prototype.hasOwnProperty.call(DOSSIER_SPEC, field);
}

/** @param {string} changeField */
export function isDossierChangeField(changeField) {
  return Object.prototype.hasOwnProperty.call(DOSSIER_CHANGE_FIELDS, changeField);
}

/**
 * The record field a change field writes, or null for anything that is no
 * change field.
 *
 * @param {string} changeField
 * @returns {string | null}
 */
export function dossierFieldForChangeField(changeField) {
  return (
    /** @type {Partial<Record<string, string>>} */ (DOSSIER_CHANGE_FIELDS)[changeField] ?? null
  );
}

/**
 * Does this code, on this field, take a number? Returns the unit or null.
 * @param {string} field
 * @param {string | string[]} value
 * @returns {DossierUnit | null}
 */
export function dossierUnitFor(field, value) {
  const spec = DOSSIER_SPEC[field];
  if (!spec || Array.isArray(value)) return null;
  return spec.amountFor[value] ?? null;
}

/** Days per month of a leap year: `229` is a real day in some years, so it is one. */
const MONTH_LENGTHS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * The calendar day an MMDD integer names, or null when it names none — the
 * one reading of the `month_day` unit (`1001` → 1 October, `1231` → 31
 * December, `1301` / `431` / `10.5` → null).
 *
 * @param {unknown} monthDay
 * @returns {{ month: number, day: number } | null}
 */
export function monthDayParts(monthDay) {
  if (typeof monthDay !== 'number' || !Number.isInteger(monthDay)) return null;
  const month = Math.floor(monthDay / 100);
  const day = monthDay % 100;
  if (month < 1 || month > 12 || day < 1 || day > MONTH_LENGTHS[month - 1]) return null;
  return { month, day };
}

/**
 * The day BEFORE an MMDD — the last day a "vóór {date}" deadline still allows
 * (`1001` → `930`, `1201` → `1130`, `301` → `228`: a deadline has no year, so
 * the eve of 1 March is read in a common year). Null when the input names no
 * day or is 1 January (whose eve is in another year).
 *
 * @param {unknown} monthDay
 * @returns {number | null}
 */
export function monthDayEve(monthDay) {
  const parts = monthDayParts(monthDay);
  if (!parts || (parts.month === 1 && parts.day === 1)) return null;
  if (parts.day > 1) return parts.month * 100 + parts.day - 1;
  const month = parts.month - 1;
  return month * 100 + (month === 2 ? 28 : MONTH_LENGTHS[month - 1]);
}

/**
 * The day AFTER an MMDD — the first day a "uiterlijk {date}" / "tot en met
 * {date}" clause makes too late (`1130` → `1201`, `930` → `1001`, `1231` →
 * `101`: the year turns). Read like monthDayEve, in a common year: 28 and 29
 * February both end February (→ `301`), because a deadline has no year. Null
 * when the input names no day.
 *
 * @param {unknown} monthDay
 * @returns {number | null}
 */
export function monthDayNext(monthDay) {
  const parts = monthDayParts(monthDay);
  if (!parts) return null;
  const common = parts.month === 2 ? 28 : MONTH_LENGTHS[parts.month - 1];
  if (parts.day < common) return parts.month * 100 + parts.day + 1;
  return parts.month === 12 ? 101 : (parts.month + 1) * 100 + 1;
}

/**
 * The most days a month offers in the yearless calendar (`2` → 29: a yearless
 * 29 February is a day) — the bound of a day picker. 0 for a number that is no
 * month.
 *
 * @param {unknown} month
 * @returns {number}
 */
export function monthDayMonthLength(month) {
  return typeof month === 'number' && Number.isInteger(month) && month >= 1 && month <= 12
    ? MONTH_LENGTHS[month - 1]
    : 0;
}

/** @param {string} s */
const collapse = (s) => String(s).replace(/\s+/g, ' ').trim();

/**
 * Validate and normalise ONE value against a field's spec.
 *
 * @param {DossierFieldSpec | undefined} spec
 * @param {unknown} raw
 * @returns {{ value: string | string[], amount: number | null, note: string } | null}
 */
function normalizeWithSpec(spec, raw) {
  if (!spec || !raw || typeof raw !== 'object') return null;
  const r = /** @type {Record<string, unknown>} */ (raw);

  let value;
  if (spec.multi) {
    const list = Array.isArray(r.value) ? r.value : typeof r.value === 'string' ? [r.value] : null;
    if (!list) return null;
    const chosen = new Set();
    for (const v of list) {
      if (typeof v !== 'string' || !spec.values.includes(v)) return null;
      chosen.add(v);
    }
    if (chosen.size === 0) return null;
    value = spec.values.filter((v) => chosen.has(v));
  } else {
    if (typeof r.value !== 'string' || !spec.values.includes(r.value)) return null;
    value = r.value;
  }

  const unit = Array.isArray(value) ? null : (spec.amountFor[value] ?? null);
  let amount = null;
  if (unit) {
    const n = typeof r.amount === 'number' ? r.amount : Number(r.amount);
    if (r.amount === null || r.amount === undefined || r.amount === '' || !Number.isFinite(n)) {
      return null;
    }
    if (n < 0 || n > DOSSIER_AMOUNT_MAX) return null;
    if (unit === 'month_day') {
      // A real day, exactly — never rounded into one. 1 January is refused: a
      // cancellation "vóór 1 januari" is no deadline before year end (`none`).
      if (!monthDayParts(n) || n === 101) return null;
      amount = n;
    } else {
      amount = unit === 'kwh' || unit === 'count' ? Math.round(n) : Math.round(n * 100) / 100;
      if (unit === 'count' && amount < 1) return null;
      // After rounding: € 0,004 is € 0,00, and that is no fee either.
      if (spec.positiveFor?.includes(/** @type {string} */ (value)) && amount <= 0) return null;
    }
  }

  const note = typeof r.note === 'string' ? collapse(r.note) : '';
  if (note.length > DOSSIER_NOTE_MAX) return null;
  const codes = Array.isArray(value) ? value : [value];
  if (!note && codes.some((c) => spec.noteFor.includes(c))) return null;

  return { value, amount, note };
}

/**
 * Validate and normalise ONE submitted value for a field. `null` means invalid
 * — the caller names the field in its error. Accepts the wire shape
 * `{ value, amount?, note? }` (what the portal posts and the drawer holds) and
 * an already-normalised entry alike. Evidence and provenance are not part of
 * the value and are ignored here.
 *
 * Rules: the code(s) must exist on the field (a LEGACY code still does — a
 * stored record must survive a save; isWritableDossierCode decides who may
 * write one); a multi field takes a non-empty, deduplicated list in spec order;
 * a code that takes an amount REQUIRES a finite number in [0,
 * DOSSIER_AMOUNT_MAX] (money rounded to cents, kWh and counts to whole numbers,
 * a `month_day` a real day other than 1 January, exactly), every other code
 * carries `amount: null`; the note is one line, trimmed, ≤ DOSSIER_NOTE_MAX,
 * and REQUIRED for the codes in `noteFor` — `other` everywhere (an "other"
 * without a word of explanation is a shrug, not a fact) and an exit `fee`
 * (whose note says per what).
 *
 * Known fields only: a field this module does not know (a retired one among
 * them) never normalises here. A note is public text, so one that names the
 * restricted party (containsRestrictedName) makes the value invalid — refused
 * on every write path, and dropped from every public read — except the
 * `inboeker` note, which names the back-end party on purpose (2026-10-08).
 *
 * @param {string} field
 * @param {unknown} raw
 * @returns {{ value: string | string[], amount: number | null, note: string } | null}
 */
export function normalizeDossierValue(field, raw) {
  const normalized = normalizeWithSpec(DOSSIER_SPEC[field], raw);
  return normalized && field !== 'inboeker' && containsRestrictedName(normalized.note)
    ? null
    : normalized;
}

/**
 * May this audience WRITE this code? The one rule for the portal submit
 * (`'provider'`) and the fill script / drawer (`'editorial'`):
 *   - legacy codes: nobody (they are read, never written);
 *   - editorial-only codes (`not_stated`): the redactie only;
 *   - derived fields, and fields this module does not know: nobody — they
 *     have no change path.
 * A list (the multi field) is writable when every code in it is.
 *
 * @param {string} field
 * @param {string | readonly string[]} code
 * @param {'provider' | 'editorial'} audience
 * @returns {boolean}
 */
export function isWritableDossierCode(field, code, audience) {
  const spec = DOSSIER_SPEC[field];
  if (!spec || isDerivedDossierField(field)) return false;
  if (audience !== 'editorial' && isEditorialDossierField(field)) return false;
  const codes = typeof code === 'string' ? [code] : Array.isArray(code) ? code : [];
  if (codes.length === 0) return false;
  return codes.every(
    (c) =>
      typeof c === 'string' &&
      spec.values.includes(c) &&
      !spec.legacy.includes(c) &&
      (audience === 'editorial' || !spec.editorialOnly.includes(c))
  );
}

/**
 * A normalised value that carries a legacy code (publicDossier drops it).
 *
 * @param {string} field
 * @param {string | string[]} value
 * @returns {boolean}
 */
function isLegacyValue(field, value) {
  const spec = DOSSIER_SPEC[field];
  if (!spec) return false;
  const codes = Array.isArray(value) ? value : [value];
  return codes.some((c) => spec.legacy.includes(c));
}

/**
 * Two values (normalised or raw wire shapes) describe the same fact?
 * Order-insensitive for a multi field; amount compared after normalisation.
 * @param {unknown} a
 * @param {unknown} b
 * @param {string} field
 */
export function dossierValuesEqual(field, a, b) {
  const na = normalizeDossierValue(field, a);
  const nb = normalizeDossierValue(field, b);
  if (!na || !nb) return na === nb;
  const va = Array.isArray(na.value) ? na.value.join(',') : na.value;
  const vb = Array.isArray(nb.value) ? nb.value.join(',') : nb.value;
  return va === vb && na.amount === nb.amount && na.note === nb.note;
}

/**
 * Provenance as stored: a known source, and an ISO `at` or null.
 * @param {unknown} raw
 * @returns {{ source: DossierSource, at: string | null }}
 */
function readProvenance(raw) {
  const r = raw && typeof raw === 'object' ? /** @type {Record<string, unknown>} */ (raw) : {};
  const source = r.source === 'provider' ? 'provider' : 'editorial';
  const at =
    typeof r.at === 'string' && !Number.isNaN(Date.parse(r.at))
      ? new Date(r.at).toISOString()
      : null;
  return { source, at };
}

/* global URL -- the browser, Node and the bake all have it */

/**
 * An absolute http(s) URL within DOSSIER_EVIDENCE_URL_MAX, trimmed — or null.
 * `new URL` rejects relative paths and garbage; the protocol check keeps
 * javascript:/data: out of a link an admin will click.
 *
 * @param {unknown} raw
 * @returns {string | null}
 */
export function normalizeEvidenceUrl(raw) {
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  if (!v || v.length > DOSSIER_EVIDENCE_URL_MAX) return null;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname ? v : null;
  } catch {
    return null;
  }
}

/** The evidence parts, in the order an entry carries them. */
const EVIDENCE_KEYS = /** @type {const} */ (['sourceUrl', 'quote', 'verifiedOn', 'sourceKind']);

/**
 * One evidence part, normalised — or null when it is no valid value.
 *   sourceUrl   an absolute http(s) URL ≤ DOSSIER_EVIDENCE_URL_MAX
 *   quote       the verbatim passage, one line ≤ DOSSIER_QUOTE_MAX
 *   verifiedOn  an ISO date ('2026-10-07', or a full ISO timestamp), kept as
 *               given: the day the fill's re-verify last re-found the quote
 *   sourceKind  one of EVIDENCE_SOURCE_KINDS (absent = website)
 *
 * @param {'sourceUrl' | 'quote' | 'verifiedOn' | 'sourceKind'} key
 * @param {unknown} raw
 * @returns {string | null}
 */
function normalizeEvidencePart(key, raw) {
  if (key === 'sourceUrl') return normalizeEvidenceUrl(raw);
  if (key === 'sourceKind') return normalizeEvidenceKind(raw);
  if (typeof raw !== 'string') return null;
  if (key === 'quote') {
    const quote = collapse(raw);
    return quote && quote.length <= DOSSIER_QUOTE_MAX ? quote : null;
  }
  const date = raw.trim();
  return /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/.test(date) &&
    !Number.isNaN(Date.parse(date))
    ? date
    : null;
}

/**
 * The EVIDENCE of one fact: where it was read (`sourceUrl`), the verbatim
 * passage (`quote`), when the quote was last re-found (`verifiedOn`) and how it
 * was read (`sourceKind`: website, document, mail, telefoon). Each
 * part is kept only when valid — a bad URL drops the URL, never the fact.
 * Never public: only the editors (dossierForEditor) and the record carry it.
 *
 * @param {unknown} raw  an entry, or anything carrying the evidence parts
 * @returns {{ sourceUrl?: string, quote?: string, verifiedOn?: string, sourceKind?: EvidenceSourceKind }}
 */
export function normalizeDossierEvidence(raw) {
  /** @type {{ sourceUrl?: string, quote?: string, verifiedOn?: string, sourceKind?: EvidenceSourceKind }} */
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  const r = /** @type {Record<string, unknown>} */ (raw);
  for (const key of EVIDENCE_KEYS) {
    const value = normalizeEvidencePart(key, r[key]);
    // A string per part; for `sourceKind` only a known kind gets through.
    if (value) /** @type {Record<string, string>} */ (out)[key] = value;
  }
  return out;
}

/**
 * The evidence of a posted entry merged PER PART with what is on disk: a part
 * the post names (even empty = cleared) follows the post; a part it leaves out
 * keeps the disk's value when the fact is unchanged — an editor that knows
 * only some of the parts (or none) never wipes the rest — and is gone when the
 * fact changed (the old evidence proved the old fact).
 *
 * @param {unknown} posted
 * @param {unknown} disk
 * @param {boolean} kept  the fact itself is unchanged
 * @returns {{ sourceUrl?: string, quote?: string, verifiedOn?: string, sourceKind?: EvidenceSourceKind }}
 */
function mergeEvidence(posted, disk, kept) {
  const p =
    posted && typeof posted === 'object' ? /** @type {Record<string, unknown>} */ (posted) : {};
  const d = disk && typeof disk === 'object' ? /** @type {Record<string, unknown>} */ (disk) : {};
  /** @type {{ sourceUrl?: string, quote?: string, verifiedOn?: string, sourceKind?: EvidenceSourceKind }} */
  const out = {};
  for (const key of EVIDENCE_KEYS) {
    const value =
      key in p
        ? normalizeEvidencePart(key, p[key])
        : kept
          ? normalizeEvidencePart(key, d[key])
          : null;
    // A string per part; for `sourceKind` only a known kind gets through.
    if (value) /** @type {Record<string, string>} */ (out)[key] = value;
  }
  // A re-verify date vouches for the quote it re-found: a new source or quote
  // without a date of its own is not verified yet.
  if (!('verifiedOn' in p) && out.verifiedOn) {
    /** @param {'sourceUrl' | 'quote'} key */
    const same = (key) => (out[key] ?? null) === normalizeEvidencePart(key, d[key]);
    if (!same('sourceUrl') || !same('quote')) delete out.verifiedOn;
  }
  return out;
}

/**
 * The public reading of a set of entries: valid, non-legacy entries with their
 * provenance, evidence stripped, in `fields` order; null when none is left.
 *
 * @param {readonly string[]} fields
 * @param {unknown} raw
 * @returns {Record<string, DossierEntry> | null}
 */
function publicEntries(fields, raw) {
  if (!raw || typeof raw !== 'object') return null;
  /** @type {Record<string, DossierEntry>} */
  const out = {};
  for (const field of fields) {
    const entry = /** @type {Record<string, unknown>} */ (raw)[field];
    const normalized = normalizeDossierValue(field, entry);
    if (!normalized || isLegacyValue(field, normalized.value)) continue;
    out[field] = { ...normalized, ...readProvenance(entry) };
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The editor's reading: like publicEntries, but legacy codes stay (an editor
 * shows them read-only and a save keeps them) and so does the evidence.
 *
 * @param {readonly string[]} fields
 * @param {unknown} raw
 * @returns {Record<string, DossierEntry> | null}
 */
function editorEntries(fields, raw) {
  if (!raw || typeof raw !== 'object') return null;
  /** @type {Record<string, DossierEntry>} */
  const out = {};
  for (const field of fields) {
    const entry = /** @type {Record<string, unknown>} */ (raw)[field];
    const normalized = normalizeDossierValue(field, entry);
    if (!normalized) continue;
    out[field] = { ...normalized, ...readProvenance(entry), ...normalizeDossierEvidence(entry) };
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The dossier as every public surface reads it: valid entries only, each with
 * its provenance, in DOSSIER_FIELDS order. `null` when the record holds no
 * usable entry at all, so a consumer can drop the whole block instead of
 * rendering an empty list. Unknown fields and malformed entries are dropped
 * silently — a hand-edited record must never take a page down. Since
 * 2026-10-07 also dropped: the RETIRED fields, LEGACY codes (both read as "not
 * checked") and the evidence (`sourceUrl` / `quote` never leave the record).
 *
 * ONE derivation for /api/providers, GET /api/provider-profile/:slug, both
 * prerender builders and GET /api/partner/provider: the same input yields the
 * same object at bake time and at request time (hydration-mismatch defense).
 *
 * @param {{ dossier?: unknown } | null | undefined} provider
 * @returns {Record<string, DossierEntry> | null}
 */
export function publicDossier(provider) {
  const raw = provider && typeof provider === 'object' ? provider.dossier : null;
  return publicEntries(DOSSIER_FIELDS, raw);
}

/**
 * The dossier as an EDITOR seeds from it (admin drawer): every valid entry of a
 * current field WITH its evidence, legacy codes included — the drawer shows a
 * legacy code read-only and must not lose it, or its evidence, on the first
 * unrelated save. Never a public payload. A retired field is not here, and the
 * admin save does not keep one either (stampDossierProvenance).
 *
 * @param {{ dossier?: unknown } | null | undefined} provider
 * @returns {Record<string, DossierEntry> | null}
 */
export function dossierForEditor(provider) {
  const raw = provider && typeof provider === 'object' ? provider.dossier : null;
  return editorEntries(DOSSIER_FIELDS, raw);
}

/**
 * One OFFER's own terms (`contract.terms`), as every public surface reads
 * them — the publicDossier rules over OFFER_TERM_FIELDS.
 * Null when the offer overrides nothing.
 *
 * @param {{ terms?: unknown } | null | undefined} contract
 * @returns {Record<string, DossierEntry> | null}
 */
export function publicOfferTerms(contract) {
  const raw = contract && typeof contract === 'object' ? contract.terms : null;
  return publicEntries(OFFER_TERM_FIELDS, raw);
}

/**
 * One offer's overrides as the drawer's "Afwijkende voorwaarden" seed from
 * them: evidence and legacy codes kept (dossierForEditor's rule).
 *
 * @param {{ terms?: unknown } | null | undefined} contract
 * @returns {Record<string, DossierEntry> | null}
 */
export function offerTermsForEditor(contract) {
  const raw = contract && typeof contract === 'object' ? contract.terms : null;
  return editorEntries(OFFER_TERM_FIELDS, raw);
}

/**
 * The dossier of ONE ROW: the provider's public dossier with the offer's
 * overrides laid over it (D6 — a row inherits what it does not override),
 * rebuilt in DOSSIER_FIELDS order so both payload mirrors emit identical bytes.
 * An offer's own rhythm rides last: its note and its date belong to the row's
 * facts; its value is the row's `payoutFrequency` (offerPayoutFrequency).
 *
 * Returns the SAME object it was given when the offer overrides nothing, so a
 * client refetch equal to the baked seed keeps the seeded object (`sameJson`)
 * and the page does not re-render after hydration.
 *
 * @template {Record<string, unknown> | null | undefined} D
 * @param {D} dossier   the provider's public dossier (publicDossier / publicSignupFacts)
 * @param {{ terms?: unknown } | null | undefined} contract
 * @returns {D | Record<string, unknown>}
 */
export function withOfferTerms(dossier, contract) {
  const overrides = publicOfferTerms(contract);
  if (!overrides) return dossier;
  const base =
    dossier && typeof dossier === 'object' ? /** @type {Record<string, unknown>} */ (dossier) : {};
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const field of ROW_DOSSIER_FIELDS) {
    if (overrides[field]) out[field] = overrides[field];
    else if (base[field]) out[field] = base[field];
  }
  return out;
}

/**
 * The payout rhythm of ONE ROW: the offer's own where it has one, else what the
 * provider pays out by. ONE rule for the comparer rows, whose `payoutFrequency`
 * carries it to every reader of a row, and the offers on the profile page.
 *
 * @template {string | null | undefined} F
 * @param {F} providerFrequency  the provider's public one (publicPayoutFrequency), handed back as is
 * @param {{ terms?: unknown } | null | undefined} contract
 * @returns {F | string}
 */
export function offerPayoutFrequency(providerFrequency, contract) {
  const own = publicOfferTerms(contract)?.payoutFrequency;
  return own ? /** @type {string} */ (own.value) : providerFrequency;
}

/**
 * The contract's deviating business model — the exclamation icon next to the
 * commission on every card — or undefined for the norm. First the offer's
 * pricing model (normalizeOfferPricing, src/lib/offerPricing.js): `tiered` = a
 * staffel; `kwh_fee` = a commission plus a vastrecht per kWh; `fixed_rate` = a
 * fixed €/kWh payout the shown % is derived from (flagged only when there IS
 * a shown %); `zero_commission` = a subscription, genuine 0%. A legacy
 * `fixedRatePayout` flag on a record without a model of its own still flags
 * `fixed_rate` next to its hand-typed % (which the cost math reads as a
 * commission: no rate is stored). Then the dossier: `other` = the provider's
 * dossier price model is "Anders" (flagged only next to a shown %, like
 * `fixed_rate`).
 *
 * ONE rule for /api/providers, GET /api/provider-profile/:slug and both
 * prerender builders. The dossier entry goes through the same normaliser as
 * publicDossier, so an "Anders" without a note flags nothing here either.
 *
 * @param {{ dossier?: unknown } | null | undefined} provider
 * @param {{ commissionFee?: unknown, zeroCommission?: unknown, fixedRatePayout?: unknown, pricing?: unknown }} contract
 * @param {number | null} commissionFee the PUBLIC commission (null = unknown)
 * @returns {import('../types/api/providerTerms.js').DeviatingModel | undefined}
 */
export function deviatingModelOf(provider, contract, commissionFee) {
  const pricing = normalizeOfferPricing(contract);
  if (pricing?.model === 'tiered') return 'tiered';
  if (pricing?.model === 'commission_plus_kwh_fee') return 'kwh_fee';
  const legacyFixedRate = contract.fixedRatePayout === true && !ownOfferPricing(contract);
  if ((pricing?.model === 'fixed_rate' || legacyFixedRate) && commissionFee !== null) {
    return 'fixed_rate';
  }
  if (pricing?.model === 'subscription') return 'zero_commission';
  const raw =
    provider && typeof provider.dossier === 'object' && provider.dossier
      ? /** @type {Record<string, unknown>} */ (provider.dossier).priceModel
      : null;
  const priceModel = normalizeDossierValue('priceModel', raw);
  if (priceModel?.value === 'other' && commissionFee !== null) return 'other';
  return undefined;
}

/** @param {unknown} dossier */
export function dossierEntryCount(dossier) {
  const pub = publicDossier({ dossier });
  return pub ? Object.keys(pub).length : 0;
}

/**
 * Apply one approved `prov:dossier…` row to a record's dossier. `change` is the
 * wire value (`{ value, amount?, note? }`) or `null` = clear the field. Returns
 * the next dossier object (never mutates), or the input when the change is
 * invalid — an invalid value cannot become a public fact by being approved.
 *
 * @param {unknown} dossier          the record's current dossier (may be absent)
 * @param {string} field             a DOSSIER_FIELDS entry
 * @param {unknown} change
 * @param {{ source: DossierSource, at: string }} provenance
 * @returns {Record<string, unknown>}
 */
export function applyDossierChange(dossier, field, change, provenance) {
  const base =
    dossier && typeof dossier === 'object'
      ? { .../** @type {Record<string, unknown>} */ (dossier) }
      : {};
  if (!isDossierField(field)) return base;
  if (change === null) {
    delete base[field];
    return base;
  }
  const normalized = normalizeDossierValue(field, change);
  if (!normalized) return base;
  base[field] = { ...normalized, source: provenance.source, at: provenance.at };
  return base;
}

/**
 * Stamp one set of posted entries against what is on disk (the shared core of
 * the two admin-save stamps below). Per field: an entry whose fact is unchanged
 * keeps the provenance it had; a new or changed one is stamped. The evidence is
 * merged per part (mergeEvidence): a part the post names (`sourceUrl`,
 * `quote`, `verifiedOn` — even empty = cleared) follows the post; a part it
 * leaves out keeps the disk's value when the fact is unchanged — an editor
 * that predates (some of) the evidence fields must not wipe them — and is gone
 * when the fact changed (the old evidence proved the old fact).
 *
 * @param {readonly string[]} fields
 * @param {unknown} before
 * @param {unknown} after
 * @param {{ source: DossierSource, at: string }} provenance
 * @returns {Record<string, DossierEntry>}
 */
function stampEntries(fields, before, after, provenance) {
  const prev =
    before && typeof before === 'object' ? /** @type {Record<string, unknown>} */ (before) : {};
  const posted =
    after && typeof after === 'object' ? /** @type {Record<string, unknown>} */ (after) : {};
  /** @type {Record<string, DossierEntry>} */
  const out = {};
  for (const field of fields) {
    const next = posted[field];
    const normalized = normalizeDossierValue(field, next);
    if (!normalized) continue;
    const kept = !!prev[field] && dossierValuesEqual(field, prev[field], normalized);
    const evidence = mergeEvidence(next, prev[field], kept);
    out[field] = kept
      ? { ...normalized, ...readProvenance(prev[field]), ...evidence }
      : { ...normalized, source: provenance.source, at: provenance.at, ...evidence };
  }
  return out;
}

/**
 * The admin providers save is a full-record POST from the CMS's in-memory copy,
 * so the server cannot know which dossier entries the admin actually touched —
 * it can only compare. An entry whose fact (value, amount, note) is unchanged
 * keeps the provenance it had (a provider-confirmed value stays the
 * provider's); an entry that is new or changed is stamped with `provenance`
 * (the editor, now). Entries the client dropped are gone; malformed entries are
 * dropped too. Evidence: see stampEntries.
 *
 * Only the fields this module knows are kept. A retired entry a record still
 * carries (`noticePeriod`, `exitFee`, `firstPayout`) — the editor's or the
 * provider's own — is dropped by the save: since 2026-10-14 nothing reads it.
 *
 * Returns undefined when nothing remains, so the record carries no empty
 * `dossier: {}`.
 *
 * @param {unknown} before   the dossier on disk
 * @param {unknown} after    the dossier in the posted record
 * @param {{ source: DossierSource, at: string }} provenance
 * @returns {Record<string, unknown> | undefined}
 */
export function stampDossierProvenance(before, after, provenance) {
  const out = stampEntries(DOSSIER_FIELDS, before, after, provenance);
  return Object.keys(out).length ? out : undefined;
}

/**
 * The same stamp for one OFFER's overrides (`contract.terms`), OFFER_TERM_FIELDS
 * only. Undefined when the offer overrides nothing, so the contract carries no
 * empty `terms: {}`.
 *
 * @param {unknown} before   the contract's terms on disk
 * @param {unknown} after    the terms in the posted contract
 * @param {{ source: DossierSource, at: string }} provenance
 * @returns {Record<string, DossierEntry> | undefined}
 */
export function stampOfferTermsProvenance(before, after, provenance) {
  const out = stampEntries(OFFER_TERM_FIELDS, before, after, provenance);
  return Object.keys(out).length ? out : undefined;
}

// ── Dutch labels for admin-only surfaces (review card, review mails) ─────────
// The public site and the portal render the codes through i18n (`dossier.*`
// keys in both baselines); these tables exist because the admin UI and the
// e-mails are Dutch-only and cannot reach the i18n tree from the server.

const NOT_STATED_NL = 'Niet vermeld in de voorwaarden';

export const DOSSIER_FIELD_LABELS_NL = Object.freeze({
  cancelDeadline: 'Opzegdatum',
  afterTerm: 'Na afloop van de looptijd',
  exitCost: 'Kosten bij eerder stoppen',
  payoutTiming: 'Moment van uitbetaling',
  inboeker: 'Inboeker',
  priceModel: 'Prijsmodel',
  minimumVolume: 'Minimale afname',
  signupRequirements: 'Nodig om aan te melden',
  payoutFrequency: 'Uitbetalingsfrequentie',
});

export const DOSSIER_VALUE_LABELS_NL = Object.freeze({
  cancelDeadline: Object.freeze({
    none: 'Opzeggen kan tot en met 31 december',
    // The FIRST day that is too late, so never "uiterste": that invites the
    // last day. formatDossierEntryNl prints the day straight after "vóór".
    before_date: 'Opzegging moet binnen zijn vóór',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
  }),
  afterTerm: Object.freeze({
    ends: 'Loopt af',
    renews_yearly: 'Stilzwijgend verlengd met telkens één jaar',
    renews_same_term: 'Stilzwijgend verlengd met dezelfde looptijd',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
    // Legacy since 2026-10-07: still printed, never offered.
    renews_monthly: 'Stilzwijgend verlengd, daarna maandelijks opzegbaar (vervallen)',
  }),
  exitCost: Object.freeze({
    none: 'Geen kosten',
    forfeit_year_payout: 'De uitbetaling over dat jaar vervalt',
    fee: 'Opzegvergoeding',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
  }),
  payoutTiming: Object.freeze({
    during_year: 'Tijdens het laadjaar',
    after_year: 'Na afloop van het laadjaar',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
  }),
  inboeker: Object.freeze({
    self: 'Zelf',
    other: 'Andere inboekdienstverlener',
    not_stated: 'Niet vermeld',
  }),
  // Reworded 2026-09-16 (WS-11): the three labels said WHAT the number is, not
  // what the customer's payment is BASED on, which is the question the field
  // asks. This table is keyed by CODE and is Dutch-only (admin card + review
  // mails), so it is edited in place; the i18n twins moved to `_v2` leaves
  // instead — see DOSSIER_VALUE_LABEL_KEY above.
  priceModel: Object.freeze({
    market_minus_commission: 'Marktprijs van de ERE minus uw commissie',
    fixed_per_kwh: 'Vaste vergoeding per geladen kWh, los van de marktprijs',
    fixed_per_ere: 'Vaste vergoeding per ERE, los van de marktprijs',
    subscription:
      'Abonnement: vast bedrag per maand of jaar, de volledige ERE-opbrengst gaat naar de klant',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
  }),
  minimumVolume: Object.freeze({
    none: 'Geen minimum',
    kwh_per_year: 'Minimaal aantal kWh per jaar',
    charge_points: 'Minimaal aantal laadpunten',
    other: 'Anders',
    not_stated: NOT_STATED_NL,
  }),
  signupRequirements: Object.freeze({
    ean: 'EAN-code van de aansluiting',
    meter_reading: 'Meterstand of foto van de meter',
    serial_number: 'Serienummer van de laadpaal',
    type_plate_photo: 'Foto van het typeplaatje van de laadpaal',
    mid_certificate: 'MID-certificaat of typegoedkeuring',
    kvk: 'KvK-nummer',
    energy_contract: 'Contract met de energieleverancier',
    other: 'Anders',
  }),
  // The code IS the Dutch word: the vocabulary of src/lib/payout.js.
  payoutFrequency: Object.freeze(
    Object.fromEntries(PAYOUT_FREQUENCIES.map((frequency) => [frequency, frequency]))
  ),
});

/**
 * Dutch number for the admin/mail formatter. Intl is fine here: these strings
 * never reach a hydrating page.
 * @param {number} n
 * @param {number} decimals
 */
function nlNumber(n, decimals) {
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/**
 * Dutch month names for the admin/mail formatter — this module is plain JS
 * and cannot reach src/lib/formatters.ts (whose formatMonthDay is the site's
 * twin, with its own table, for the hydrating pages).
 */
const NL_MONTHS = Object.freeze([
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
]);

/**
 * The amount with its unit, Dutch. `€ 75`, `€ 0,12 per kWh`, `2.500 kWh per
 * jaar`, `3 laadpunten`, `vóór 1 oktober`.
 * @param {DossierUnit} unit
 * @param {number} amount
 */
export function formatDossierAmountNl(unit, amount) {
  switch (unit) {
    case 'eur':
      return `€ ${nlNumber(amount, Number.isInteger(amount) ? 0 : 2)}`;
    case 'eur_per_kwh':
      return `€ ${nlNumber(amount, 2)} per kWh`;
    case 'eur_per_ere':
      return `€ ${nlNumber(amount, 2)} per ERE`;
    case 'kwh':
      return `${nlNumber(amount, 0)} kWh per jaar`;
    case 'count':
      return `${nlNumber(amount, 0)} ${amount === 1 ? 'laadpunt' : 'laadpunten'}`;
    case 'month_day': {
      const day = nlMonthDay(amount);
      return day ? `vóór ${day}` : String(amount);
    }
    default:
      return String(amount);
  }
}

/**
 * A `month_day` as "1 oktober"; null when it is no real day.
 *
 * @param {number} amount
 * @returns {string | null}
 */
function nlMonthDay(amount) {
  const parts = monthDayParts(amount);
  return parts ? `${parts.day} ${NL_MONTHS[parts.month - 1]}` : null;
}

/**
 * One entry as the review card and the review mails print it (Dutch). Invalid
 * or absent → '—'. `Anders` prints the note as the value; any other code with
 * a note prints it in brackets. A field this module does not know prints '—'.
 *
 * @param {string} field
 * @param {unknown} entry   wire value or stored entry
 * @returns {string}
 */
export function formatDossierEntryNl(field, entry) {
  const n = normalizeWithSpec(DOSSIER_SPEC[field], entry);
  if (!n) return '—';
  /** @param {string} c */
  const prose = (c) => isProseCode(field, c);
  const labels =
    /** @type {Partial<Record<string, Readonly<Record<string, string>>>>} */ (
      DOSSIER_VALUE_LABELS_NL
    )[field] ?? {};
  const codes = Array.isArray(n.value) ? n.value : [n.value];
  const parts = codes.map((c) => {
    if (prose(c)) return `Anders: ${n.note}`;
    const unit = dossierUnitFor(field, c);
    const label = labels[c] ?? c;
    if (!unit || n.amount === null) return label;
    // A deadline's label ends on "vóór": "Opzegging moet binnen zijn vóór 1 oktober".
    const day = unit === 'month_day' ? nlMonthDay(n.amount) : null;
    return day ? `${label} ${day}` : `${label} — ${formatDossierAmountNl(unit, n.amount)}`;
  });
  const text = parts.join(', ');
  return codes.some(prose) || !n.note ? text : `${text} (${n.note})`;
}
